import { BASE_PALETTES, type BaseThemePalette } from '@bn-games/theming';

/**
 * Mahjong theme id. Iteration 1 ships a single wood-table theme (the same
 * Monte Carlo base palette + wood pattern domino uses); a named union keeps
 * the settings field and `THEMES` registry ready to grow the same way the
 * other apps' theme sets do.
 */
export type MahjongThemeName = 'greenFelt';

/**
 * Mahjong theme: extends the cross-game base palette with table + tile
 * tokens the canvas renderers (T6+) read directly.
 *
 * - `boardWoodBase/Dark/Light` + `borderWoodBase/Dark/Light` (from the base
 *   palette) paint the wood-textured table + backdrop via
 *   `@bn-games/render-helpers`' `getWoodPattern`/`getBorderWoodPattern` —
 *   same wood palette domino's table uses.
 * - `tileFace*` paint the face-up tile bodies; `tileBack*` paint edge-on
 *   opponent stacks and the wall. Suit/honor face ART itself is a composited
 *   sprite (`render/tileSprites.ts`), not a themeable ink.
 * - `dragonRedColor` sources both the ActionBar's Ron button AND every tile's
 *   corner rank numeral / wind letter (`RenderPalette.cornerNumeralColor`) —
 *   a fixed red regardless of suit, the universal tile-face convention.
 * - `compassText` labels the seat compass.
 * - `selectedRing` outlines the picked hand tile; `validClaimGlow` marks a
 *   discard that a call (Pung/Chi/Ron) can claim.
 */
export interface Theme extends BaseThemePalette {
  readonly monogram: string;

  readonly tileFaceBase: string;
  readonly tileFaceHighlight: string;
  readonly tileFaceShadow: string;
  readonly tileFaceBorder: string;

  readonly tileBackBase: string;
  readonly tileBackPattern: string;
  readonly tileBackBorder: string;

  /** ActionBar's Ron button + every tile's corner numeral/wind letter. */
  readonly dragonRedColor: string;

  readonly compassText: string;

  readonly selectedRing: string;
  readonly validClaimGlow: string;
}

export const GREEN_FELT_THEME: Theme = {
  ...BASE_PALETTES.monte,
  name: 'monte',
  label: 'Monte Carlo',
  tagline: 'High stakes on the Riviera',
  monogram: 'M',

  tileFaceBase: '#f6efdd',
  tileFaceHighlight: '#fffbee',
  tileFaceShadow: '#cabf9e',
  tileFaceBorder: '#b7a878',

  tileBackBase: '#1f7d5c',
  tileBackPattern: '#125540',
  tileBackBorder: '#0a3b2c',

  dragonRedColor: '#b3241f',

  compassText: '#f4e6c0',

  selectedRing: '#f0d898',
  validClaimGlow: 'rgba(240, 216, 152, 0.85)',
};

export const THEMES: Record<MahjongThemeName, Theme> = {
  greenFelt: GREEN_FELT_THEME,
};
